// Bekleyen Onaylar — kullanıcının kendi seviyesindeki bekleyen belgeleri listeler.
// Onayla / Reddet / Geri Gönder eylemleriyle akışı ilerletir.
// Belge satırına tıklandığında salt-okunur detay açılır (düzenleme/silme yok).
import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { Button, ListPageHeader, EmptyState, Modal, FormField, FilterBar } from '../components/ui';
import DataTable from '../components/DataTable';
import { fmtAmount } from '../utils/numberFormat';

const MODULE_LABELS = {
  invoice: 'Fatura',
  waybill: 'İrsaliye',
  order:   'Sipariş',
  quote:   'Teklif',
};
const DIRECTION_LABELS = {
  sales: 'Satış',
  purchase: 'Alış',
};

// source_table → sayfa anahtarı eşlemesi (MainLayout navigation için)
const SOURCE_TO_PAGE = {
  sales_invoices:    { page: 'sales',         param: 'invoiceId' },
  purchase_invoices: { page: 'purchases',     param: 'invoiceId' },
  sales_waybills:    { page: 'waybills-sales', param: 'waybillId' },
  purchase_waybills: { page: 'waybills-purchase', param: 'waybillId' },
  sales_orders:      { page: 'orders-incoming', param: 'orderId' },
  purchase_orders:   { page: 'orders-outgoing', param: 'orderId' },
  sales_quotes:      { page: 'quotes-outgoing', param: 'quoteId' },
  purchase_quotes:   { page: 'quotes-incoming', param: 'quoteId' },
};

// source_table → API base path (belge GET/PUT için)
const SOURCE_TO_API = {
  sales_invoices:    '/sales-invoices',
  purchase_invoices: '/purchase-invoices',
  sales_waybills:    '/sales-waybills',
  purchase_waybills: '/purchase-waybills',
  sales_orders:      '/sales-orders',
  purchase_orders:   '/purchase-orders',
  sales_quotes:      '/sales-quotes',
  purchase_quotes:   '/purchase-quotes',
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('tr-TR') : '—';

export default function PendingApprovalsPage({ onNavigate } = {}) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ module: '', direction: '', date_from: '', date_to: '' });
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [actionModal, setActionModal] = useState(null); // { row, action }
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/approvals/pending');
      if (r.data.success) setList(r.data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!actionModal) return;
    setSaving(true);
    try {
      const r = await api.post('/approvals/' + actionModal.row.id + '/action', { action: actionModal.action, note });
      // Onaylandı + finalize → belgeyi PUT ile yeniden kaydet ki save handler movements (stok/cari/yevmiye) yazsın.
      // Backend'de needsApproval gate'i bu durumda atlanıyor (document_approvals.status='approved' kontrolü).
      if (r.data?.doc_status === 'approved' && r.data?.source_table && r.data?.source_id) {
        const apiBase = SOURCE_TO_API[r.data.source_table];
        if (apiBase) {
          try {
            const inv = await api.get(apiBase + '/' + r.data.source_id);
            if (inv.data?.success && inv.data.data) {
              const doc = inv.data.data;
              await api.put(apiBase + '/' + r.data.source_id, { ...doc, status: 'approved' });
            }
          } catch (e) {
            console.error('movement apply:', e);
            alert('Onay verildi ama hareketler yazılırken hata: ' + (e.response?.data?.message || e.message));
          }
        }
      }
      setActionModal(null); setNote('');
      await load();
    } catch (e) { alert(e.response?.data?.message || 'Hata'); }
    finally { setSaving(false); }
  };

  const filtered = list.filter(r => {
    // Modül/yön/tarih filtreleri
    if (filters.module && r.module !== filters.module) return false;
    if (filters.direction && r.direction !== filters.direction) return false;
    if (filters.date_from && r.created_at && new Date(r.created_at) < new Date(filters.date_from)) return false;
    if (filters.date_to && r.created_at && new Date(r.created_at) > new Date(new Date(filters.date_to).getTime() + 24*60*60*1000)) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.hierarchy_name || '').toLowerCase().includes(q)
        || (r.module || '').toLowerCase().includes(q)
        || (r.account_title || '').toLowerCase().includes(q)
        || (r.document_number || '').toLowerCase().includes(q)
        || (r.internal_number || '').toLowerCase().includes(q);
  });

  const openDetail = (row) => {
    const cfg = SOURCE_TO_PAGE[row.source_table];
    if (!cfg || !onNavigate) return;
    // Salt-okunur deep-link → SalesInvoicePage canEdit'i bloke eder
    onNavigate(cfg.page, row.source_id, { viewOnly: true });
  };

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Bekleyen Onaylar"
        unit="belge"
        count={filtered.length}
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Belge no, cari, hiyerarşi ara..."
        onRefresh={load}
        refreshing={loading}
      />

      <FilterBar
        show={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'module', type: 'select', label: 'Modül',
            options: [{ value: '', label: 'Tümü' }, ...Object.entries(MODULE_LABELS).map(([k, v]) => ({ value: k, label: v }))] },
          { key: 'direction', type: 'select', label: 'Yön',
            options: [{ value: '', label: 'Tümü' }, ...Object.entries(DIRECTION_LABELS).map(([k, v]) => ({ value: k, label: v }))] },
          { key: 'date_from', type: 'date', label: 'Oluşturma — Başlangıç' },
          { key: 'date_to',   type: 'date', label: 'Oluşturma — Bitiş' },
        ]}
      />

      {filtered.length === 0 && !loading ? (
        <EmptyState title="Bekleyen onay yok" description="Sizin onay vermeniz gereken belge bulunmuyor" compact />
      ) : (
        <div className="stocks-table-wrap">
          <DataTable
            pageKey="pending-approvals"
            tableClassName="stocks-table"
            rowClassName="stocks-table-row"
            loading={loading}
            data={filtered}
            rowKey="id"
            onRowClick={openDetail}
            columns={[
              { id: 'module',  header: 'Belge Tipi',     accessor: r => MODULE_LABELS[r.module] + ' / ' + DIRECTION_LABELS[r.direction], sortable: true, width: 170 },
              { id: 'docno',   header: 'Belge No',       accessor: r => r.document_number || r.internal_number || '—', sortable: true, width: 160,
                cell: r => <span className="stock-code" style={{ fontFamily: 'monospace' }}>{r.document_number || r.internal_number || '—'}</span> },
              { id: 'docdate', header: 'Tarih',          accessor: 'doc_date', sortable: true, width: 110,
                cell: r => fmtDate(r.doc_date) },
              { id: 'cari',    header: 'Cari',           accessor: 'account_title', sortable: true,
                cell: r => <span className="stock-name-cell">{r.account_title || '—'}{r.account_code ? <span style={{ color:'var(--text-tertiary)', fontSize:'0.78rem', marginLeft:6 }}>({r.account_code})</span> : null}</span> },
              { id: 'level',   header: 'Mevcut Seviye',  accessor: r => 'Seviye ' + r.current_level + (r.current_level_name ? ' — ' + r.current_level_name : ''), sortable: true, width: 180 },
              { id: 'amount',  header: 'Tutar',          accessor: r => parseFloat(r.total_amount) || 0, sortable: true, width: 130,
                headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
                cell: r => <strong style={{ fontFamily: 'monospace' }}>{fmtAmount(r.total_amount || 0)}</strong> },
              { id: 'actions', header: 'İşlem',          sortable: false, width: 280,
                cell: r => (
                  <span style={{ display: 'inline-flex', gap: 6 }}>
                    <Button size="sm" onClick={(e) => { e.stopPropagation(); setActionModal({ row: r, action: 'approved' }); }}
                      style={{ background: 'rgba(16,185,129,0.15)', color: 'var(--success)', borderColor: 'rgba(16,185,129,0.4)' }}>
                      ✓ Onayla
                    </Button>
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setActionModal({ row: r, action: 'sent_back' }); }}>
                      ↩ Geri Gönder
                    </Button>
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setActionModal({ row: r, action: 'rejected' }); }}
                      style={{ color: 'var(--danger)' }}>
                      ✕ Reddet
                    </Button>
                  </span>
                ) },
            ]}
          />
        </div>
      )}

      {actionModal && (
        <Modal
          open
          onClose={() => { setActionModal(null); setNote(''); }}
          title={actionModal.action === 'approved' ? 'Onayla' : actionModal.action === 'rejected' ? 'Reddet' : 'Geri Gönder'}
          cancelAction={{ label: 'İptal', onClick: () => { setActionModal(null); setNote(''); } }}
          primaryAction={
            actionModal.action === 'approved'
              ? { label: 'Onayla', onClick: submit, loading: saving }
              : actionModal.action === 'rejected'
              ? null  // reddet primary değil destructive
              : { label: 'Geri Gönder', onClick: submit, loading: saving }
          }
          destructiveAction={
            actionModal.action === 'rejected'
              ? { label: 'Reddet', onClick: submit }
              : null
          }>
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ fontSize: '0.88rem' }}>
              Belge: <strong>{MODULE_LABELS[actionModal.row.module]} / {DIRECTION_LABELS[actionModal.row.direction]}</strong> — Seviye {actionModal.row.current_level}
            </div>
            {actionModal.row.document_number && (
              <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                Belge No: <strong style={{ fontFamily: 'monospace' }}>{actionModal.row.document_number}</strong>
                {actionModal.row.account_title ? ' · Cari: ' + actionModal.row.account_title : ''}
              </div>
            )}
            <FormField label="Not (opsiyonel)">
              <textarea rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="Açıklama..." />
            </FormField>
          </div>
        </Modal>
      )}
    </div>
  );
}
